import { Note } from '../api/notes'
import NoteCard from './NoteCard'

interface NotesGridProps {
  notes: Note[]
  isLoading?: boolean
  emptyMessage?: string
  emptyIcon?: 'notes' | 'star' | 'archive' | 'trash' | 'home'
  onToggleStar?: (noteId: string) => void
  onToggleHome?: (noteId: string) => void
  selectedIds?: string[]
  onSelect?: (noteId: string) => void
  selectionMode?: boolean
}

const emptyIcons = {
  notes: (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  star: (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  archive: (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  ),
  trash: (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  home: (
    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
}

export default function NotesGrid({
  notes,
  isLoading = false,
  emptyMessage = 'No notes found',
  emptyIcon = 'notes',
  onToggleStar,
  onToggleHome,
  selectedIds = [],
  onSelect,
  selectionMode = false,
}: NotesGridProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
            <div className="p-3 sm:p-4 border-b border-gray-100">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="flex gap-1">
                <div className="h-4 bg-gray-100 rounded w-12"></div>
                <div className="h-4 bg-gray-100 rounded w-16"></div>
              </div>
            </div>
            <div className="p-3 sm:p-4">
              <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-100 rounded w-5/6 mb-2"></div>
              <div className="h-4 bg-gray-100 rounded w-4/6"></div>
            </div>
            <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-t border-gray-100">
              <div className="h-3 bg-gray-100 rounded w-20"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Empty state
  if (!notes || notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          {emptyIcons[emptyIcon]}
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">{emptyMessage}</h3>
        <p className="text-gray-500 max-w-sm">
          {emptyIcon === 'home' && 'Pin your favorite notes to the home screen for quick access.'}
          {emptyIcon === 'star' && 'Star notes to find them quickly later.'}
          {emptyIcon === 'archive' && 'Archived notes will appear here.'}
          {emptyIcon === 'trash' && 'Deleted notes will appear here for 30 days.'}
          {emptyIcon === 'notes' && 'Create your first note to get started.'}
        </p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
      {notes.map((note) => (
        <NoteCard
          key={note.id}
          note={note}
          onToggleStar={onToggleStar}
          onToggleHome={onToggleHome}
          isSelected={selectedIds.includes(note.id)}
          onSelect={onSelect}
          selectionMode={selectionMode}
        />
      ))}
    </div>
  )
}
